import * as THREE from "../../libs/three.js/build/three.module.js";
import {Annotation} from "../Annotation.js";
import {CameraMode, EventTopic, UniqueElementNames} from "../defines.js";
import {View} from "./View.js";
import {Utils} from "../utils.js";
import {EventDispatcher} from "../EventDispatcher.js";
import * as Potree from "../Potree";


export class Scene extends EventDispatcher{

	constructor(viewer){
		super();

		this.annotations = new Annotation();
		this.viewer = viewer;

		this.scene = new THREE.Scene();
		this.sceneBG = new THREE.Scene();
		this.scenePointCloud = new THREE.Scene();

		this.cameraP = new THREE.PerspectiveCamera(this.fov, 1, 0.1, 1000*1000);
		this.cameraO = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000*1000);
		this.cameraVR = new THREE.PerspectiveCamera();
		this.cameraBG = new THREE.Camera();
		this.cameraScreenSpace = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
		this.cameraMode = CameraMode.PERSPECTIVE;
		this.overrideCamera = null;
		this.pointclouds = [];
		this.calculatedBoundingBox = null;

		this.measurements = [];
		this.profiles = [];
		this.volumes = [];
		this.defaultClipVolume = null;
		this.polygonClipVolumes = [];
		this.cameraAnimations = [];
		this.orientedImages = [];
		this.images360 = [];
		this.geopackages = [];

		this.fpControls = null;
		this.orbitControls = null;
		this.earthControls = null;
		this.geoControls = null;
		this.deviceControls = null;
		this.inputHandler = null;

		this.view = new View();

		this.directionalLight = null;

		this.initialize();

		this.viewer.addEventListener('cliptask_changed', () => {
			if (this.volumes.length < 1) return;

			const addedVolume = this.volumes[this.volumes.length - 1];

			if (!addedVolume.initialState) {
				addedVolume.setInitialPosition();
			}
		});
	}

	estimateHeightAt (position) {
		let height = null;
		let fromSpacing = Infinity;

		for (let pointcloud of this.pointclouds) {
			if (pointcloud.root.geometryNode === undefined) {
				continue;
			}

			let pHeight = null;
			let pFromSpacing = Infinity;

			let lpos = position.clone().sub(pointcloud.position);
			lpos.z = 0;
			let ray = new THREE.Ray(lpos, new THREE.Vector3(0, 0, 1));

			let stack = [pointcloud.root];
			while (stack.length > 0) {
				let node = stack.pop();
				let box = node.getBoundingBox();

				let inside = ray.intersectBox(box);

				if (!inside) {
					continue;
				}

				let h = node.geometryNode.mean.z +
					pointcloud.position.z +
					node.geometryNode.boundingBox.min.z;

				if (node.geometryNode.spacing <= pFromSpacing) {
					pHeight = h;
					pFromSpacing = node.geometryNode.spacing;
				}

				for (let index of Object.keys(node.children)) {
					let child = node.children[index];
					if (child.geometryNode) {
						stack.push(node.children[index]);
					}
				}
			}

			if (height === null || pFromSpacing < fromSpacing) {
				height = pHeight;
				fromSpacing = pFromSpacing;
			}
		}

		return height;
	}

	getBoundingBox(pointclouds = this.pointclouds){
		let box = new THREE.Box3();

		this.scenePointCloud.updateMatrixWorld(true);
		this.referenceFrame.updateMatrixWorld(true);

		for (let pointcloud of pointclouds) {
			pointcloud.updateMatrixWorld(true);

			let pointcloudBox = pointcloud.pcoGeometry.tightBoundingBox ? pointcloud.pcoGeometry.tightBoundingBox : pointcloud.boundingBox;
			let boxWorld = Utils.computeTransformedBoundingBox(pointcloudBox, pointcloud.matrixWorld);
			box.union(boxWorld);
		}

		return box;
	}

	getMeasurementName(name) {
		let measurements = this.measurements;
		for(let i = 0; i < 1000; i++) {
			let newName = name + "_" + i;
			if(!measurements.some(x => x.name === newName)) {
				return newName;
			}
		}
	}

	getVolumeName(name) {
		let volumes = this.volumes;
		for(let i = 0; i < 1000; i++) {
			let newName = name + "_" + i;
			if(!volumes.some(x => x.name === newName)) {
				return newName;
			}
		}
	}

	getProfileName(name) {
		let profiles = this.profiles;
		for(let i = 0; i < 1000; i++) {
			let newName = name + "_" + i;
			if(!profiles.some(x => x.name === newName)) {
				return newName;
			}
		}
	}

	addPointCloud (pointcloud) {
		this.pointclouds.push(pointcloud);
		this.scenePointCloud.add(pointcloud);
		this.recalculateSceneBoundingBox();

		this.dispatchEvent({
			type: 'pointcloud_added',
			pointcloud: pointcloud
		});
	}

	addVolume (volume) {
		this.volumes.push(volume);
		this.toggleDefaultClipVolume(false);
		this.dispatchEvent({
			'type': 'volume_added',
			'scene': this,
			'volume': volume
		});
	}

	addOrientedImages(images){
		this.orientedImages.push(images);
		this.scene.add(images.node);

		this.dispatchEvent({
			'type': 'oriented_images_added',
			'scene': this,
			'images': images
		});
	};

	removeOrientedImages(images){
		let index = this.orientedImages.indexOf(images);
		if (index > -1) {
			this.orientedImages.splice(index, 1);

			this.dispatchEvent({
				'type': 'oriented_images_removed',
				'scene': this,
				'images': images
			});
		}
	};

	add360Images(images){
		this.images360.push(images);
		this.scene.add(images.node);

		this.dispatchEvent({
			'type': '360_images_added',
			'scene': this,
			'images': images
		});
	}

	remove360Images(images){
		let index = this.images360.indexOf(images);
		if (index > -1) {
			this.images360.splice(index, 1);

			this.dispatchEvent({
				'type': '360_images_removed',
				'scene': this,
				'images': images
			});
		}
	}

	addGeopackage(geopackage){
		this.geopackages.push(geopackage);
		this.scene.add(geopackage.node);

		this.dispatchEvent({
			'type': 'geopackage_added',
			'scene': this,
			'geopackage': geopackage
		});
	};

	removeGeopackage(geopackage){
		let index = this.geopackages.indexOf(geopackage);
		if (index > -1) {
			this.geopackages.splice(index, 1);

			this.dispatchEvent({
				'type': 'geopackage_removed',
				'scene': this,
				'geopackage': geopackage
			});
		}
	};

	removeVolume (volume) {
		let index = this.volumes.indexOf(volume);
		if (index > -1) {
			this.volumes.splice(index, 1);
			this.toggleDefaultClipVolume(this.volumes.length === 0);
			this.dispatchEvent({
				'type': 'volume_removed',
				'scene': this,
				'volume': volume
			});
		}
	};

	removeVolumes() {
		this.volumes.forEach(x => {
			this.dispatchEvent({
				'type': 'volume_removed',
				'scene': this,
				'volume': x
			});
		});
		this.volumes = [];
		this.toggleDefaultClipVolume(true);
	}

	addCameraAnimation(animation) {
		this.cameraAnimations.push(animation);
		this.dispatchEvent({
			'type': 'camera_animation_added',
			'scene': this,
			'animation': animation
		});
	};

	removeCameraAnimation(animation){
		let index = this.cameraAnimations.indexOf(volume);
		if (index > -1) {
			this.cameraAnimations.splice(index, 1);

			this.dispatchEvent({
				'type': 'camera_animation_removed',
				'scene': this,
				'animation': animation
			});
		}
	};

	addPolygonClipVolume(volume){
		this.polygonClipVolumes.push(volume);
		this.dispatchEvent({
			"type": "polygon_clip_volume_added",
			"scene": this,
			"volume": volume
		});
	};

	removePolygonClipVolume(volume){
		let index = this.polygonClipVolumes.indexOf(volume);
		if (index > -1) {
			this.polygonClipVolumes.splice(index, 1);
			this.dispatchEvent({
				"type": "polygon_clip_volume_removed",
				"scene": this,
				"volume": volume
			});
		}
	};

	addMeasurement(measurement){
		measurement.lengthUnit = this.lengthUnit;
		measurement.lengthUnitDisplay = this.lengthUnitDisplay;
		this.measurements.push(measurement);
		this.dispatchEvent({
			'type': 'measurement_added',
			'scene': this,
			'measurement': measurement
		});
	};

	removeMeasurement (measurement) {
		let index = this.measurements.indexOf(measurement);
		if (index > -1) {
			this.measurements.splice(index, 1);
			this.dispatchEvent({
				'type': 'measurement_removed',
				'scene': this,
				'measurement': measurement
			});
		}
	}

	removeMeasurements() {
		this.measurements.forEach(x => {
			this.dispatchEvent({
				'type': 'measurement_removed',
				'scene': this,
				'measurement': x
			});
		});
		this.measurements = [];
	}

	addProfile (profile) {
		this.profiles.push(profile);
		this.dispatchEvent({
			'type': 'profile_added',
			'scene': this,
			'profile': profile
		});
	}

	removeProfile (profile) {
		let index = this.profiles.indexOf(profile);
		if (index > -1) {
			this.profiles.splice(index, 1);
			this.dispatchEvent({
				'type': 'profile_removed',
				'scene': this,
				'profile': profile
			});
		}
	}

	removeAllMeasurements () {
		while (this.measurements.length > 0) {
			this.removeMeasurement(this.measurements[0]);
		}

		while (this.profiles.length > 0) {
			this.removeProfile(this.profiles[0]);
		}

		while (this.volumes.length > 0) {
			this.removeVolume(this.volumes[0]);
		}
	}

	removeAllClipVolumes(){
		let clipVolumes = this.volumes.filter(volume => volume.clip === true);
		for(let clipVolume of clipVolumes){
			this.removeVolume(clipVolume);
		}

		while(this.polygonClipVolumes.length > 0){
			this.removePolygonClipVolume(this.polygonClipVolumes[0]);
		}
	}

	getActiveCamera() {

		if(this.overrideCamera){
			return this.overrideCamera;
		}

		if(this.cameraMode === CameraMode.PERSPECTIVE){
			return this.cameraP;
		}else if(this.cameraMode === CameraMode.ORTHOGRAPHIC){
			return this.cameraO;
		}else if(this.cameraMode === CameraMode.VR){
			return this.cameraVR;
		}

		return null;
	}

	calculateBoundingBoxForPointclouds(pointclouds) {
		let result = {
			min: { x: Number.MAX_SAFE_INTEGER, y: Number.MAX_SAFE_INTEGER, z: Number.MAX_SAFE_INTEGER },
			max: { x: Number.MIN_SAFE_INTEGER, y: Number.MIN_SAFE_INTEGER, z: Number.MIN_SAFE_INTEGER },
		}
		for (const pointcloud of pointclouds) {
			const originalBoundingBox = pointcloud.pcoGeometry.tightBoundingBox;

			const transformationMatrix = pointcloud.matrixWorld;
			const boundingBox = originalBoundingBox.clone().applyMatrix4(transformationMatrix);

			// Min
			result.min.x = Math.min((boundingBox.min.x), result.min.x);
			result.min.y = Math.min((boundingBox.min.y), result.min.y);
			result.min.z = Math.min((boundingBox.min.z), result.min.z);

			// Max
			result.max.x = Math.max((boundingBox.max.x), result.max.x);
			result.max.y = Math.max((boundingBox.max.y), result.max.y);
			result.max.z = Math.max((boundingBox.max.z), result.max.z);
		}

		return this.mapBoundingBoxToSizeAndPosition(result);
	}

	mapBoundingBoxToSizeAndPosition(boundingBox) {
		if (boundingBox.min.x === Infinity) {
			return null;
		}

		// Calculate size
		const size = {
			x: boundingBox.max.x - boundingBox.min.x,
			y: boundingBox.max.y - boundingBox.min.y,
			z: boundingBox.max.z - boundingBox.min.z,
		};

		// Bounding Box is centered, move it to the corner
		const position = {
			x: boundingBox.min.x + size.x / 2,
			y: boundingBox.min.y + size.y / 2,
			z: boundingBox.min.z + size.z / 2,
		}

		return {
			size,
			position,
		}
	}

	recalculateSceneBoundingBox() {
		const boundingBox = this.getBoundingBox(this.pointclouds);
		this.calculatedBoundingBox = this.mapBoundingBoxToSizeAndPosition(boundingBox);

		this.dispatchEvent({
			type: EventTopic.BoundingBoxRecalculated,
			calculatedBoundingBox: this.calculatedBoundingBox
		});

		this.resetDefaultClipVolumePosition();
	}

	toggleDefaultClipVolume(enabled) {
		if (this.defaultClipVolume) {
			this.defaultClipVolume.clip = enabled;
		}
	}

	resetDefaultClipVolumePosition() {
		if (this.defaultClipVolume && this.calculatedBoundingBox && this.calculatedBoundingBox.size) {
			const volume = this.defaultClipVolume;
			const sceneBoundingBox = this.calculatedBoundingBox;

			volume.position.set(sceneBoundingBox.position.x, sceneBoundingBox.position.y, sceneBoundingBox.position.z);
			volume.scale.set(sceneBoundingBox.size.x, sceneBoundingBox.size.y, sceneBoundingBox.size.z);
			volume.update();
		}
	}

	createDefaultClipVolume() {
		const volume  = new Potree.BoxVolume();
		volume.name = UniqueElementNames.DefaultClipVolume;

		const sceneBoundingBox = this.calculatedBoundingBox;

		if (sceneBoundingBox === null) {
			volume.scale.set(1, 1, 1);
			volume.position.set(1, 2, 3);
		}  else {
			volume.scale.set(sceneBoundingBox.size.x, sceneBoundingBox.size.y, sceneBoundingBox.size.z);
			volume.position.set(sceneBoundingBox.position.x, sceneBoundingBox.position.y, sceneBoundingBox.position.z);
		}

		volume.clip = true;
		volume.visible = false;
		volume.modifieable = false;

		this.defaultClipVolume = volume;
		this.scene.add(this.defaultClipVolume);
	}


	initialize(){

		this.referenceFrame = new THREE.Object3D();
		this.referenceFrame.matrixAutoUpdate = false;
		this.scenePointCloud.add(this.referenceFrame);

		this.cameraP.up.set(0, 0, 1);
		this.cameraP.position.set(1000, 1000, 1000);
		this.cameraO.up.set(0, 0, 1);
		this.cameraO.position.set(1000, 1000, 1000);
		//this.camera.rotation.y = -Math.PI / 4;
		//this.camera.rotation.x = -Math.PI / 6;
		this.cameraScreenSpace.lookAt(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, 1, 0));

		this.directionalLight = new THREE.DirectionalLight( 0xffffff, 0.5 );
		this.directionalLight.position.set( 10, 10, 10 );
		this.directionalLight.lookAt( new THREE.Vector3(0, 0, 0));
		this.scenePointCloud.add( this.directionalLight );

		let light = new THREE.AmbientLight( 0x555555 ); // soft white light
		this.scenePointCloud.add( light );

		{ // background
			let texture = Utils.createBackgroundTexture(512, 512);

			texture.minFilter = texture.magFilter = THREE.NearestFilter;
			texture.minFilter = texture.magFilter = THREE.LinearFilter;
			let bg = new THREE.Mesh(
				new THREE.PlaneBufferGeometry(2, 2, 1),
				new THREE.MeshBasicMaterial({
					map: texture
				})
			);
			bg.material.depthTest = false;
			bg.material.depthWrite = false;
			this.sceneBG.add(bg);
		}

		{ // lights
			{
				let light = new THREE.DirectionalLight(0xffffff);
				light.position.set(10, 10, 1);
				light.target.position.set(0, 0, 0);
				this.scene.add(light);
			}

			{
				let light = new THREE.DirectionalLight(0xffffff);
				light.position.set(-10, 10, 1);
				light.target.position.set(0, 0, 0);
				this.scene.add(light);
			}

			{
				let light = new THREE.DirectionalLight(0xffffff);
				light.position.set(0, -10, 20);
				light.target.position.set(0, 0, 0);
				this.scene.add(light);
			}
		}

		this.recalculateSceneBoundingBox();
		this.createDefaultClipVolume();
	}

	addAnnotation(position, args = {}){
		if(position instanceof Array){
			args.position = new THREE.Vector3().fromArray(position);
		} else if (position.x != null) {
			args.position = position;
		}
		let annotation = new Annotation(args);
		this.annotations.add(annotation);

		return annotation;
	}

	getAnnotations () {
		return this.annotations;
	};

	removeAnnotation(annotationToRemove) {
		this.annotations.remove(annotationToRemove);
	}

	getSelectedVolume() {
		return this.volumes.find((volume) => volume.isSelected);
	}
};
